// S2-0f.1-S2: Agent Overview — identity, domain, composition, performance, readiness, version
import { useState } from 'react';
// Badge will be used when Role strip is added in future sprint
import type { AgentDetail } from '../types/eval';
import './AgentOverview.css';

interface Props {
  detail: AgentDetail;
  onRunEval?: () => void;
}

// Composition node types
type CompPanel = 'llm' | 'skill' | 'schema' | 'tools' | 'mcp';

export function AgentOverview({ detail, onRunEval }: Props) {
  const [activePanel, setActivePanel] = useState<CompPanel | null>('llm');

  const togglePanel = (panel: CompPanel) => {
    setActivePanel(prev => prev === panel ? null : panel);
  };

  // Defensive defaults — handle old API responses missing new fields
  const runStats = detail.run_stats ?? { total_runs: 0, avg_quality: 0, avg_cost_cents: 0, total_cost_cents: 0, last_run_at: null };

  // Performance status helpers
  const qualityStatus = runStats.avg_quality >= 0.85 ? 'good' : runStats.avg_quality >= 0.7 ? 'warn' : 'neutral';
  const costStatus = runStats.avg_cost_cents <= 1 ? 'good' : runStats.avg_cost_cents <= 10 ? 'warn' : 'neutral';

  // Format cost display
  const formatCost = (cents: number) => cents < 1 ? `<0.1¢` : `${cents.toFixed(1)}¢`;

  // Time ago helper
  const timeAgo = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  // Schema layer tag type
  const layerTag = (idx: number) => {
    if (idx === 0) return { cls: 'tag-required', label: 'Base' };
    if (idx === 1) return { cls: 'tag-vertical', label: 'Vertical' };
    return { cls: 'tag-client', label: 'Client' };
  };

  return (
    <div className="agent-overview">

      {/* ── SECTION 1: Identity ── */}
      <div className="overview-identity">
        <div className="overview-identity-row">
          <div>
            <div>
              <span className="overview-name">{detail.name}</span>
              <span className="overview-purpose">{detail.purpose || 'enrichment'}</span>
            </div>
            <div className="overview-desc">{detail.description}</div>
            <div className="overview-created">Agent ID: {detail.id}</div>
          </div>
        </div>
      </div>

      {/* ── SECTION 2: Domain/Role strip — deferred to future sprint (needs Role registry) ── */}

      {/* ── SECTION 3: Composition graph ── */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <div className="overview-section-label">
          Agent Composition
          <span className="overview-section-hint">— click to explore</span>
        </div>

        <div className="comp-graph">
          {([
            { id: 'llm' as CompPanel, icon: 'comp-node-icon-llm', count: 1, label: 'Model', svg: <circle cx="8" cy="8" r="5" stroke="currentColor" strokeWidth="1.2" fill="none" /> },
            { id: 'skill' as CompPanel, icon: 'comp-node-icon-skill', count: 1, label: 'Skill', svg: <><rect x="3" y="4" width="10" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.2" fill="none" /><path d="M5 8L7 10L11 6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none" /></> },
            { id: 'schema' as CompPanel, icon: 'comp-node-icon-schema', count: Array.isArray(detail.schema_layers) ? detail.schema_layers.length : 0, label: 'Schema Layers', svg: <><path d="M3 4H13M3 8H13M3 12H9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" fill="none" /></> },
            { id: 'tools' as CompPanel, icon: 'comp-node-icon-tools', count: detail.expected_workflow?.length || 0, label: 'Tools', svg: <path d="M10 2L14 6L6 14H2V10L10 2Z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none" /> },
            { id: 'mcp' as CompPanel, icon: 'comp-node-icon-mcp', count: 0, label: 'Plugins / MCP', svg: <><path d="M6 2V5M10 2V5M3 5H13V13H3V5Z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none" /></> },
          ]).map(node => (
            <div
              key={node.id}
              className={`comp-node${activePanel === node.id ? ' active' : ''}`}
              onClick={() => togglePanel(node.id)}
            >
              <div className={`comp-node-icon ${node.icon}`}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">{node.svg}</svg>
              </div>
              <div className="comp-node-count">{node.count}</div>
              <div className="comp-node-label">{node.label}</div>
            </div>
          ))}
        </div>

        {/* Detail panels */}
        {activePanel === 'llm' && (
          <div className="comp-detail">
            <div className="comp-detail-chips">
              <span className="comp-chip"><span className="comp-chip-label">MODEL</span> claude-sonnet-4-6</span>
              <span className="comp-chip"><span className="comp-chip-label">PROVIDER</span> Anthropic Managed Agents</span>
              <span className="comp-chip"><span className="comp-chip-label">ROUTING</span> Sonnet (standard) · Haiku (claims)</span>
            </div>
          </div>
        )}

        {activePanel === 'skill' && (
          <div className="comp-detail">
            <div className="comp-detail-chips">
              <span className="comp-chip"><span className="comp-chip-label" style={{ color: 'var(--signal-400)' }}>SKILL</span> product-listing-creator</span>
              <span className="comp-chip"><span className="comp-chip-label">TYPE</span> {detail.purpose || 'enrichment'}</span>
              <span className="comp-chip"><span className="comp-chip-label">OUTPUT</span> {detail.output_fields?.length || 0} fields</span>
              <span className="comp-chip"><span className="comp-chip-label">QUALITY</span> {detail.quality_dimensions?.length || 0} dimensions</span>
            </div>
            {detail.expected_workflow && detail.expected_workflow.length > 0 && (
              <div style={{ marginTop: 12, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: 10, color: 'var(--text-muted)', marginRight: 4 }}>WORKFLOW:</span>
                {detail.expected_workflow.map((step, i) => (
                  <span key={step} style={{ display: 'contents' }}>
                    {i > 0 && <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>&rarr;</span>}
                    <span className="comp-chip"><span className="comp-chip-num">{i + 1}</span> {step}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {activePanel === 'schema' && (
          <div className="comp-detail">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(Array.isArray(detail.schema_layers) ? detail.schema_layers : []).map((layer, i) => {
                const tag = layerTag(i);
                const name = typeof layer === 'string' ? layer : layer.name;
                return (
                  <div key={name} className="comp-layer">
                    <span className="comp-layer-priority">P{i + 1}</span>
                    <div>
                      <div className="comp-layer-name">{name}</div>
                    </div>
                    <span className={`comp-layer-tag ${tag.cls}`}>{tag.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activePanel === 'tools' && (
          <div className="comp-detail">
            <div className="comp-detail-chips">
              {(detail.expected_workflow || []).map((tool, i) => (
                <span key={tool} className="comp-chip">
                  <span className="comp-chip-num">{i + 1}</span> {tool}
                </span>
              ))}
            </div>
          </div>
        )}

        {activePanel === 'mcp' && (
          <div className="comp-detail">
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
              No MCP connections configured. Plugins available in Phase 4 (Shopify, WooCommerce).
            </div>
          </div>
        )}
      </div>

      {/* ── SECTION 4: Performance ── */}
      <div className="overview-section-label">Performance</div>
      <div className="overview-perf">
        <div className={`perf-card ${runStats.total_runs > 0 ? 'good' : 'neutral'}`}>
          <div className={`perf-value ${runStats.total_runs > 0 ? 'good' : 'neutral'}`}>
            {runStats.total_runs}
          </div>
          <div className="perf-label">Total Runs</div>
        </div>

        <div className={`perf-card ${qualityStatus}`}>
          <div className={`perf-value ${qualityStatus}`}>
            {runStats.avg_quality > 0 ? runStats.avg_quality.toFixed(3) : '—'}
          </div>
          <div className="perf-label">Avg Quality</div>
        </div>

        <div className={`perf-card ${costStatus}`}>
          <div className={`perf-value ${costStatus}`}>
            {runStats.avg_cost_cents > 0 ? formatCost(runStats.avg_cost_cents) : '—'}
          </div>
          <div className="perf-label">Avg Cost / Run</div>
        </div>

        <div className="perf-card neutral">
          <div className="perf-value neutral">
            {runStats.total_cost_cents > 0 ? formatCost(runStats.total_cost_cents) : '—'}
          </div>
          <div className="perf-label">Total Cost</div>
        </div>

        <div className={`perf-card ${runStats.last_run_at ? 'good' : 'neutral'}`}>
          <div className="perf-value neutral" style={{ fontSize: 14, marginTop: 4 }}>
            {timeAgo(runStats.last_run_at)}
          </div>
          <div className="perf-label">Last Run</div>
          {detail.latest_run && (
            <div className="perf-sub" style={{
              marginTop: 6,
              background: 'var(--success-dark-bg)',
              color: 'var(--success-dark-text)',
              display: 'inline-block',
              padding: '2px 6px',
              borderRadius: 'var(--radius-sm)',
              fontSize: 10,
            }}>
              {detail.latest_run.b3_classification}
            </div>
          )}
        </div>
      </div>

      {/* ── SECTION 5: Readiness ── */}
      <div className="overview-readiness">
        <div className="overview-section-label">Readiness</div>
        <div className="overview-no-data">
          No readiness check has been run yet. Use "Check Readiness" to get a deployment verdict.
        </div>
      </div>

      {/* ── SECTION 6: Version placeholder ── */}
      <div className="overview-version">
        <div className="overview-version-icon">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M3 14V2H10L13 5V14H3Z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M10 2V5H13" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div className="overview-version-text">
          <strong>Version tracking</strong> — Agent versioning, changelogs, and rollback will be available here.
        </div>
        <span className="overview-version-tag">Sprint 3</span>
      </div>

      {/* ── ACTION BAR ── */}
      <div style={{
        display: 'flex', gap: 12, padding: '16px 24px',
        background: 'rgba(26,25,23,0.9)', backdropFilter: 'blur(12px)',
        borderTop: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-xl)',
        position: 'sticky', bottom: 24,
        boxShadow: '0 10px 15px -3px rgba(0,0,0,0.3)',
      }}>
        <button
          onClick={onRunEval}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '10px 20px', borderRadius: 'var(--radius-lg)',
            background: 'var(--primary-500)', color: 'var(--neutral-0)',
            border: 'none', fontFamily: 'var(--font-primary)',
            fontSize: 13, fontWeight: 700, cursor: 'pointer',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M5.5 3L11 8L5.5 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          Run Evaluation
        </button>
        <button
          disabled
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '10px 20px', borderRadius: 'var(--radius-lg)',
            background: 'transparent', color: 'var(--primary-400)',
            border: '1px solid var(--primary-700)', fontFamily: 'var(--font-primary)',
            fontSize: 13, fontWeight: 600, cursor: 'not-allowed', opacity: 0.4,
          }}
        >
          Check Readiness
          <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 400 }}>S2-0f.3</span>
        </button>
        <button
          disabled
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '10px 20px', borderRadius: 'var(--radius-lg)',
            background: 'transparent', color: 'var(--text-muted)',
            border: '1px solid var(--border-default)', fontFamily: 'var(--font-primary)',
            fontSize: 13, fontWeight: 600, cursor: 'not-allowed', opacity: 0.4,
          }}
        >
          History
          <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 400 }}>S2-0f.4</span>
        </button>
      </div>
    </div>
  );
}
